import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ContactlessIcon from '@mui/icons-material/Contactless';
import QrScannerDialog from './QrScannerDialog';
import NfcScanDialog from './NfcScanDialog';
import { isNfcSupported } from './nfcLogin';
import type { SyncBlocker } from '../../db/syncBlockers';

interface SyncLoginDialogProps {
    blocker: SyncBlocker;
    /**
     * Authenticate as the blocked user and release their queue. Returns a
     * localized error, or null on success.
     */
    onAuthenticate: (
        credential:
            | { kind: 'password'; username: string; password: string }
            | { kind: 'token'; token: string }
    ) => Promise<string | null>;
    onDismiss: () => void;
}

/**
 * Asks the person whose changes are stuck to prove who they are, so their queue
 * can go up under their own name.
 *
 * This is NOT a session switch: whoever is using the device stays signed in.
 * The credential is only kept for the push.
 */
export default function SyncLoginDialog({
    blocker,
    onAuthenticate,
    onDismiss
}: SyncLoginDialogProps) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [nfcOpen, setNfcOpen] = useState(false);
    const nfcSupported = isNfcSupported();

    async function run(
        credential: Parameters<typeof onAuthenticate>[0]
    ): Promise<void> {
        setError(null);
        setBusy(true);
        const failure = await onAuthenticate(credential);
        if (failure) {
            setError(failure);
        }
        setBusy(false);
    }

    async function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        await run({
            kind: 'password',
            username: blocker.username,
            password
        });
    }

    async function handleToken(token: string) {
        setScanOpen(false);
        setNfcOpen(false);
        await run({ kind: 'token', token });
    }

    return (
        <>
            <Dialog open maxWidth="xs" fullWidth onClose={onDismiss}>
                <DialogTitle>
                    <FormattedMessage id="sync.blocked.title" />
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                        >
                            <FormattedMessage
                                id="sync.blocked.body"
                                values={{
                                    count: blocker.count,
                                    name: <strong>{blocker.name}</strong>
                                }}
                            />
                        </Typography>

                        {error && <Alert severity="error">{error}</Alert>}

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <TextField
                                    label={
                                        <FormattedMessage id="auth.username" />
                                    }
                                    value={blocker.username}
                                    fullWidth
                                    disabled
                                />
                                <TextField
                                    label={
                                        <FormattedMessage id="auth.password" />
                                    }
                                    type="password"
                                    value={password}
                                    onChange={(event) =>
                                        setPassword(event.target.value)
                                    }
                                    fullWidth
                                    autoFocus
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={busy || !password}
                                >
                                    {busy ? (
                                        <FormattedMessage id="auth.login.loading" />
                                    ) : (
                                        <FormattedMessage id="sync.blocked.submit" />
                                    )}
                                </Button>
                            </Stack>
                        </form>

                        <Divider />

                        <Stack spacing={1}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<QrCodeScannerIcon />}
                                onClick={() => setScanOpen(true)}
                                disabled={busy}
                            >
                                <FormattedMessage id="auth.qr.scanButton" />
                            </Button>
                            {nfcSupported && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ContactlessIcon />}
                                    onClick={() => setNfcOpen(true)}
                                    disabled={busy}
                                >
                                    <FormattedMessage id="auth.nfc.scanButton" />
                                </Button>
                            )}
                        </Stack>

                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                        >
                            <FormattedMessage id="sync.blocked.hint" />
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={onDismiss}
                        disabled={busy}
                    >
                        <FormattedMessage id="sync.blocked.later" />
                    </Button>
                </DialogActions>
            </Dialog>

            {scanOpen && (
                <QrScannerDialog
                    open
                    onClose={() => setScanOpen(false)}
                    onDecoded={(token) => void handleToken(token)}
                />
            )}
            {nfcOpen && (
                <NfcScanDialog
                    open
                    onClose={() => setNfcOpen(false)}
                    onToken={(token) => void handleToken(token)}
                />
            )}
        </>
    );
}
